/** Shared CubicleMediaVault — every cubicle inherits this.
 *
 *  Features:
 *   • Upload image / video / PDF via URL (Emergent Object Storage
 *     integration deferred — v1 URL-only keeps unblocking the flow).
 *   • Grid with kind badge + approval status pill.
 *   • Approve / Reject / Revision-request button trio for HOD +
 *     Producer/LP + Director + owner/admin.
 *   • Live via WebSocket channel ``cubicle:<pid>:<key>``.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Image as ImgIcon, Video, FileText, Upload, Check, X,
  RefreshCw, Clock, MessageSquare, Trash2,
} from "lucide-react";

const KIND_ICON = { image: ImgIcon, video: Video, pdf: FileText };

const STATUS_STYLE = {
  pending:             { color: "text-amber-300",   bg: "bg-amber-500/15",   icon: Clock },
  approved:            { color: "text-emerald-300", bg: "bg-emerald-500/15", icon: Check },
  rejected:            { color: "text-red-300",     bg: "bg-red-500/15",     icon: X },
  revision_requested:  { color: "text-sky-300",     bg: "bg-sky-500/15",     icon: RefreshCw },
};

export default function CubicleMediaVault({ projectId, cubicleKey,
                                            canApprove, accent }) {
  const [items, setItems] = useState([]);
  const [open, setOpen]   = useState(false);
  const [url, setUrl]     = useState("");
  const [kind, setKind]   = useState("image");
  const [caption, setCaption] = useState("");
  const [busy, setBusy]   = useState(false);
  const [note, setNote]   = useState({});     // {media_id: string}

  const load = async () => {
    try {
      const { data } = await api.get(
        `/projects/${projectId}/cubicles/${cubicleKey}/media`);
      setItems(data.items || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { void load(); }, [projectId, cubicleKey]);

  const upload = async () => {
    if (!url.trim()) { toast.error("URL required"); return; }
    setBusy(true);
    try {
      await api.post(
        `/projects/${projectId}/cubicles/${cubicleKey}/media`,
        { kind, url: url.trim(), caption: caption.trim() },
      );
      toast.success("Media added — pending approval");
      setUrl(""); setCaption(""); setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const review = async (mediaId, status) => {
    setBusy(true);
    try {
      await api.post(
        `/projects/${projectId}/cubicles/${cubicleKey}/media/${mediaId}/review`,
        { status, note: (note[mediaId] || "").trim() },
      );
      const label = { approved: "Approved", rejected: "Rejected",
                      revision_requested: "Revision requested" }[status];
      toast.success(`${label} · ${mediaId.slice(0,6)}`);
      setNote({ ...note, [mediaId]: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="cs-card rounded-2xl p-5 mt-6"
         data-testid={`media-vault-${cubicleKey}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
            Media Vault
          </div>
          <div className="text-sm text-white/70">
            Upload references · approve / revise / reject
          </div>
        </div>
        <Button onClick={() => setOpen(!open)}
                className="btn-themed h-9 px-4 rounded-full uppercase tracking-[0.2em] text-[10px]"
                data-testid={`media-upload-toggle-${cubicleKey}`}>
          <Upload size={12}/> Upload
        </Button>
      </div>

      {open && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-6 gap-3"
             data-testid={`media-upload-form-${cubicleKey}`}>
          <select value={kind} onChange={(e) => setKind(e.target.value)}
                  className="bg-black/40 border border-white/15 rounded-md px-3 h-10 text-sm text-white/85"
                  data-testid={`media-kind-${cubicleKey}`}>
            <option value="image">Image</option>
            <option value="video">Video</option>
            <option value="pdf">PDF</option>
          </select>
          <Input value={url} onChange={(e) => setUrl(e.target.value)}
                 placeholder="Media URL"
                 className="md:col-span-3 bg-black/40 border-white/15"
                 data-testid={`media-url-${cubicleKey}`}/>
          <Input value={caption} onChange={(e) => setCaption(e.target.value)}
                 placeholder="Caption (optional)"
                 className="md:col-span-2 bg-black/40 border-white/15"
                 data-testid={`media-caption-${cubicleKey}`}/>
          <Button onClick={upload} disabled={busy}
                  className="md:col-span-6 md:justify-self-end btn-themed rounded-full px-6 uppercase tracking-[0.22em] text-[11px]"
                  data-testid={`media-submit-${cubicleKey}`}>
            <Upload size={12}/> Add reference
          </Button>
        </div>
      )}

      {items.length === 0 && !open && (
        <div className="mt-4 text-xs text-white/40 border border-dashed border-white/10 rounded-lg p-6 text-center">
          No media yet — upload the first reference.
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
           data-testid={`media-grid-${cubicleKey}`}>
        {items.map((m) => {
          const KIcon = KIND_ICON[m.kind] || ImgIcon;
          const st = STATUS_STYLE[m.approval_status || "pending"] || STATUS_STYLE.pending;
          const SIcon = st.icon;
          return (
            <div key={m.id}
                 className="border border-white/10 rounded-xl overflow-hidden bg-black/30"
                 data-testid={`media-card-${m.id}`}>
              <div className="aspect-video bg-black/70 flex items-center justify-center overflow-hidden relative">
                {m.kind === "image" && m.url
                  ? <img src={m.url} alt="" className="w-full h-full object-cover"/>
                  : <KIcon size={30} className="text-white/45"/>}
                <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase inline-flex items-center gap-1 ${st.bg} ${st.color}`}
                      data-testid={`media-status-${m.id}`}>
                  <SIcon size={9}/> {(m.approval_status || "pending").replace("_", " ")}
                </span>
                <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase inline-flex items-center gap-1 bg-white/10 text-white/80">
                  <KIcon size={9}/> {m.kind}
                </span>
              </div>
              <div className="p-3 text-xs">
                <div className="text-white/85 line-clamp-2">{m.caption || "—"}</div>
                <div className="text-[10px] text-white/45 mt-1">
                  by {m.uploader_name || m.uploaded_by || "—"}
                </div>
                {m.approval_status === "approved" && m.approved_by_name && (
                  <div className="text-[10px] text-emerald-300/85 mt-1">
                    ✓ {m.approved_by_name}
                  </div>
                )}
                {m.review_note && (
                  <div className="mt-1.5 text-[11px] text-white/70 border-l-2 pl-2 border-white/15">
                    <MessageSquare size={9} className="inline mr-1"/> {m.review_note}
                  </div>
                )}
                {canApprove && (m.approval_status || "pending") !== "approved" && (
                  <div className="mt-3 space-y-2">
                    <Input value={note[m.id] || ""}
                           onChange={(e) => setNote({...note, [m.id]: e.target.value})}
                           placeholder="Review note (optional)"
                           className="bg-black/40 border-white/12 h-8 text-xs"
                           data-testid={`media-review-note-${m.id}`}/>
                    <div className="flex gap-1">
                      <button onClick={() => review(m.id, "approved")}
                              disabled={busy}
                              className="flex-1 h-7 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-[10px] uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1"
                              data-testid={`media-approve-${m.id}`}>
                        <Check size={10}/> Approve
                      </button>
                      <button onClick={() => review(m.id, "revision_requested")}
                              disabled={busy}
                              className="flex-1 h-7 rounded-md bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 text-[10px] uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1"
                              data-testid={`media-revise-${m.id}`}>
                        <RefreshCw size={10}/> Revise
                      </button>
                      <button onClick={() => review(m.id, "rejected")}
                              disabled={busy}
                              className="flex-1 h-7 rounded-md bg-red-500/15 hover:bg-red-500/25 text-red-300 text-[10px] uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1"
                              data-testid={`media-reject-${m.id}`}>
                        <X size={10}/> Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
